// ShareSheet — modal para compartir viaje con link público de solo lectura.
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Copy, Check, Share2, MessageCircle } from "lucide-react";
import { api } from "../auth/AuthContext";

export default function ShareSheet({ open, trip, onClose }) {
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [recipient, setRecipient] = useState("amor");

  useEffect(() => {
    if (!open || !trip) return;
    setCopied(false);
    // Recupera el último nombre usado por viaje
    try {
      const last = localStorage.getItem(`share-recipient-${trip.trip_id}`);
      if (last) setRecipient(last);
    } catch {}
    if (trip.share_token) {
      setToken(trip.share_token);
      return;
    }
    setLoading(true);
    api(`/api/trips/${trip.trip_id}/share`, { method: "POST" })
      .then((r) => setToken(r.share_token))
      .finally(() => setLoading(false));
  }, [open, trip]);

  const link = token ? `${window.location.origin}/v/${token}` : "";
  const cleanName = (recipient || "").trim();
  const greeting = cleanName ? `Hola ${cleanName}` : "Hola";
  const message = `${greeting}, te comparto el itinerario completo de nuestro viaje "${trip?.title}". Ábrelo en tu celular y vas a ver vuelos, hoteles, día a día, restaurantes y mapas. Funciona como app, sin instalar nada:\n\n${link}`;

  // Persist last recipient name per trip
  useEffect(() => {
    if (!trip?.trip_id) return;
    try { localStorage.setItem(`share-recipient-${trip.trip_id}`, recipient); } catch {}
  }, [recipient, trip?.trip_id]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: trip?.title, text: message, url: link });
      } catch { /* user cancelled */ }
    } else {
      copyLink();
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[1100] bg-black/40 flex items-end justify-center"
          onClick={onClose}
          data-testid="share-sheet-backdrop"
        >
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 320, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-[hsl(var(--bg))] rounded-t-3xl shadow-2xl"
            style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 24px)" }}
            data-testid="share-sheet"
          >
            <div className="px-5 py-4 border-b border-[hsl(var(--line))] flex items-center justify-between">
              <h2 className="text-base font-bold">🔗 Compartir viaje</h2>
              <button onClick={onClose} className="text-[hsl(var(--muted))] active:scale-95">
                <X size={20} />
              </button>
            </div>

            <div className="px-5 py-5">
              <p className="text-sm text-[hsl(var(--ink-soft))] leading-relaxed mb-4">
                Genera un link de <strong>solo lectura</strong>. Quien lo reciba puede ver
                tu itinerario completo, paradas, restaurantes y mapas sin necesidad de instalar nada
                ni iniciar sesión.
              </p>

              {loading && <p className="text-center text-sm text-[hsl(var(--muted))] py-6">Generando link…</p>}

              {link && (
                <>
                  <div className="card-paper p-3 mb-4 break-all" data-testid="share-link">
                    <p className="text-xs text-[hsl(var(--accent))] font-mono">{link}</p>
                  </div>

                  {/* Personalizar destinatario */}
                  <label className="block text-[11px] uppercase tracking-wider text-[hsl(var(--muted))] font-semibold mb-1.5">
                    ¿A quién se lo mandas?
                  </label>
                  <input
                    type="text"
                    value={recipient}
                    onChange={(e) => setRecipient(e.target.value)}
                    placeholder="amor, Pablo, mamá…"
                    className="w-full px-3 py-2.5 mb-3 rounded-xl border border-[hsl(var(--line))] bg-[hsl(var(--bg-card))] text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--accent))] focus:border-transparent"
                    data-testid="share-recipient-input"
                    maxLength={40}
                  />

                  {/* Vista previa del mensaje */}
                  <div className="bg-[#25D366]/5 border border-[#25D366]/20 rounded-xl p-3 mb-4" data-testid="share-message-preview">
                    <p className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted))] font-semibold mb-1.5">Vista previa</p>
                    <p className="text-xs text-[hsl(var(--ink))] whitespace-pre-wrap leading-relaxed">{message}</p>
                  </div>

                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <button
                      onClick={copyLink}
                      className="flex flex-col items-center gap-1 py-3 rounded-2xl bg-[hsl(var(--bg-card))] border border-[hsl(var(--line))] active:scale-95"
                      data-testid="share-copy"
                    >
                      {copied ? <Check size={18} className="text-[hsl(var(--accent))]" /> : <Copy size={18} />}
                      <span className="text-[10px] uppercase tracking-wider font-semibold">{copied ? "Copiado" : "Copiar link"}</span>
                    </button>
                    <a
                      href={`https://wa.me/?text=${encodeURIComponent(message)}`}
                      target="_blank"
                      rel="noreferrer"
                      className="flex flex-col items-center gap-1 py-3 rounded-2xl bg-[#25D366]/10 border border-[#25D366]/30 active:scale-95"
                      data-testid="share-whatsapp"
                    >
                      <MessageCircle size={18} className="text-[#25D366]" />
                      <span className="text-[10px] uppercase tracking-wider font-semibold">WhatsApp</span>
                    </a>
                    <button
                      onClick={nativeShare}
                      className="flex flex-col items-center gap-1 py-3 rounded-2xl bg-[hsl(var(--bg-card))] border border-[hsl(var(--line))] active:scale-95"
                      data-testid="share-native"
                    >
                      <Share2 size={18} />
                      <span className="text-[10px] uppercase tracking-wider font-semibold">Más…</span>
                    </button>
                  </div>

                  <p className="text-[11px] text-[hsl(var(--muted))] leading-relaxed mt-4">
                    💡 Quien tenga el link verá el viaje en modo solo lectura.
                    No podrá editarlo. Puedes revocar el acceso eliminando el viaje.
                  </p>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
